import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLanguage } from '../../store/searchSlice';
import { RootState } from '../../store/store';
import i18n from 'i18next';
import './SiteHeader.css';

const FLAG_BASE = 'https://sailarmada.com/wp-content/plugins/translatepress-multilingual/assets/flags/4x3/';

const languages = [
  { code: 'en', flag: 'en_GB.svg', label: 'EN' },
  { code: 'de', flag: 'de_DE.svg', label: 'DE' },
  { code: 'fr', flag: 'fr_FR.svg', label: 'FR' },
  { code: 'it', flag: 'it_IT.svg', label: 'IT' },
  { code: 'ro', flag: 'ro_RO.svg', label: 'RO' },
];

const SiteHeader: React.FC = () => {
  const location = useLocation();
  const isHeroPage = location.pathname === '/' || location.pathname.startsWith('/yacht-details/');

  const dispatch = useDispatch();
  const selectedLang = useSelector((state: RootState) => state.search.selectedLanguage) || 'en';
  const currentLang = languages.find(l => l.code === selectedLang) || languages[0];

  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(!isHeroPage);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!isHeroPage) {
      setScrolled(true);
      return;
    }
    setScrolled(window.scrollY > 30);
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHeroPage]);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      if (mobileOpen) return;
      const y = window.scrollY;
      if (y > lastY && y > 80) setHidden(true);
      else if (y < lastY) setHidden(false);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [mobileOpen]);

  // Expose header height so HeaderWithSearch can stack below it.
  useEffect(() => {
    document.documentElement.style.setProperty('--nav-height', '68px');
    return () => { document.documentElement.style.removeProperty('--nav-height'); };
  }, []);

  useEffect(() => {
    if (window.innerWidth >= 960) return; // desktop: header never visually hides
    if (hidden) {
      const t = setTimeout(() => {
        document.documentElement.style.setProperty('--nav-height', '0px');
      }, 300); // match CSS transform: 0.3s
      return () => clearTimeout(t);
    } else {
      document.documentElement.style.setProperty('--nav-height', '68px');
    }
  }, [hidden]);

  const handleLanguageChange = (code: string) => {
    dispatch(setLanguage(code));
    i18n.changeLanguage(code).catch(() => {});
  };

  return (
    <header className={`sa-header${scrolled ? ' sa-header--scrolled' : ''}${hidden ? ' sa-header--hidden' : ''}`}>
      <div className="sa-inner">

        {/* Logo — navigates to platform home */}
        <div className="sa-logo">
          <Link to="/">
            <img
              src="https://sailarmada.com/wp-content/uploads/2022/05/Logo-Alb-Transparent-200X44-px.png"
              alt="Sail Armada"
              width={201}
              height={44}
            />
          </Link>
        </div>

        {/* Desktop Nav */}
        <nav className="sa-nav" aria-label="Main navigation">
          <ul>

            <li className="sa-has-dropdown">
              <a href="https://sailarmada.com/how-it-works/" target="_blank" rel="noopener noreferrer">
                How it works <span className="sa-caret">▾</span>
              </a>
              <ul className="sa-dropdown">
                <li><a href="https://sailarmada.com/how-it-works/" target="_blank" rel="noopener noreferrer">How it works</a></li>
                <li><a href="https://sailarmada.com/know-your-boat/" target="_blank" rel="noopener noreferrer">Our Yachts</a></li>
                <li><a href="https://sailarmada.com/charter-skippers-team/" target="_blank" rel="noopener noreferrer">Our Skippers</a></li>
                <li><a href="https://sailarmada.com/faq/" target="_blank" rel="noopener noreferrer">FAQ</a></li>
              </ul>
            </li>

            <li className="sa-has-dropdown">
              <a href="https://sailarmada.com/private-sailing/" target="_blank" rel="noopener noreferrer">
                Private sailing <span className="sa-caret">▾</span>
              </a>
              <ul className="sa-dropdown">
                <li><a href="https://sailarmada.com/private-sailing/" target="_blank" rel="noopener noreferrer">Why PRIVATE?</a></li>
                <li><a href="https://sailarmada.com/tour/sailing-holidays-in-greece-for-families/" target="_blank" rel="noopener noreferrer">Greece Private Tour</a></li>
                <li><a href="https://sailarmada.com/tour/choose-your-sailing-week-italy/" target="_blank" rel="noopener noreferrer">Italy Private Tour</a></li>
                <li><a href="https://sailarmada.com/tour/croatia-go-private/" target="_blank" rel="noopener noreferrer">Croatia Private Tour</a></li>
                <li><a href="https://sailarmada.com/tour/choose-your-sailing-week-turkey/" target="_blank" rel="noopener noreferrer">Turkey Private Tour</a></li>
              </ul>
            </li>

            <li className="sa-has-dropdown">
              <a href="https://sailarmada.com/group-sailing-holidays/" target="_blank" rel="noopener noreferrer">
                Group Sailing <span className="sa-caret">▾</span>
              </a>
              <ul className="sa-dropdown">
                <li><a href="https://sailarmada.com/group-sailing-holidays/" target="_blank" rel="noopener noreferrer">Why GROUP?</a></li>
                <li><a href="https://sailarmada.com/tour/discover-tahiti/" target="_blank" rel="noopener noreferrer">Discover Tahiti / French Polynesia</a></li>
                <li><a href="https://sailarmada.com/tour/watermelon/" target="_blank" rel="noopener noreferrer">Watermelon Croatia #18</a></li>
                <li><a href="https://sailarmada.com/tour/discover/" target="_blank" rel="noopener noreferrer">Discover Sardinia</a></li>
                <li><a href="https://sailarmada.com/tour/velactive/" target="_blank" rel="noopener noreferrer">Velactive – Sailing Active Escape</a></li>
                <li><a href="https://sailarmada.com/tour/watermelon-greece/" target="_blank" rel="noopener noreferrer">Watermelon Greece #19</a></li>
                <li><a href="https://sailarmada.com/tour/wildroute/" target="_blank" rel="noopener noreferrer">Wild Route Dodecanese</a></li>
              </ul>
            </li>

            <li>
              <Link to="/" style={{ textDecoration: 'none' }}>Find a Yacht</Link>
            </li>

            <li className="sa-has-dropdown">
              <button className="sa-nav-trigger">
                Discover <span className="sa-caret">▾</span>
              </button>
              <ul className="sa-dropdown">
                <li><a href="https://sailarmada.com/reviews/" target="_blank" rel="noopener noreferrer">Customer Reviews</a></li>
                <li><a href="https://sailarmada.com/ambassadors/" target="_blank" rel="noopener noreferrer">Ambassadors</a></li>
                <li><a href="https://sailarmada.com/blog/" target="_blank" rel="noopener noreferrer">Blog</a></li>
                <li className="sa-has-nested">
                  <a href="https://sailarmada.com/destinations/" target="_blank" rel="noopener noreferrer">
                    Destinations <span className="sa-caret">▸</span>
                  </a>
                  <ul className="sa-dropdown-nested">
                    <li><a href="https://sailarmada.com/destinations/greece/" target="_blank" rel="noopener noreferrer">Greece</a></li>
                    <li><a href="https://sailarmada.com/destinations/malta/" target="_blank" rel="noopener noreferrer">Malta</a></li>
                    <li><a href="https://sailarmada.com/destinations/italy/" target="_blank" rel="noopener noreferrer">Italy</a></li>
                    <li><a href="https://sailarmada.com/destinations/turkey/" target="_blank" rel="noopener noreferrer">Turkey</a></li>
                  </ul>
                </li>
              </ul>
            </li>

            <li>
              <a href="https://sailarmada.com/contact/" target="_blank" rel="noopener noreferrer">Contact</a>
            </li>

            <li>
              <Link to="/help" style={{ textDecoration: 'none' }}>Help</Link>
            </li>

            {/* Language switcher — uses platform i18n */}
            <li className="sa-has-dropdown sa-lang">
              <button className="sa-nav-trigger">
                <img
                  src={`${FLAG_BASE}${currentLang.flag}`}
                  alt={currentLang.label}
                  width={18}
                  height={14}
                />
                <span className="sa-caret">▾</span>
              </button>
              <ul className="sa-dropdown sa-dropdown--right">
                {languages.filter(l => l.code !== selectedLang).map(lang => (
                  <li key={lang.code}>
                    <button className="sa-lang-btn" onClick={() => handleLanguageChange(lang.code)}>
                      <img
                        src={`${FLAG_BASE}${lang.flag}`}
                        alt={lang.label}
                        width={18}
                        height={14}
                      />
                      &nbsp;{lang.label}
                    </button>
                  </li>
                ))}
              </ul>
            </li>

          </ul>
        </nav>

        {/* Mobile hamburger */}
        <button
          className="sa-mobile-btn"
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <nav className="sa-mobile-menu" aria-label="Mobile navigation">
          <span className="sa-mobile-section">How it works</span>
          <a href="https://sailarmada.com/how-it-works/" target="_blank" rel="noopener noreferrer">How it works</a>
          <a href="https://sailarmada.com/know-your-boat/" target="_blank" rel="noopener noreferrer">Our Yachts</a>
          <a href="https://sailarmada.com/charter-skippers-team/" target="_blank" rel="noopener noreferrer">Our Skippers</a>
          <a href="https://sailarmada.com/faq/" target="_blank" rel="noopener noreferrer">FAQ</a>
          <span className="sa-mobile-section">Private sailing</span>
          <a href="https://sailarmada.com/private-sailing/" target="_blank" rel="noopener noreferrer">Why PRIVATE?</a>
          <a href="https://sailarmada.com/tour/sailing-holidays-in-greece-for-families/" target="_blank" rel="noopener noreferrer">Greece Private Tour</a>
          <a href="https://sailarmada.com/tour/choose-your-sailing-week-italy/" target="_blank" rel="noopener noreferrer">Italy Private Tour</a>
          <a href="https://sailarmada.com/tour/croatia-go-private/" target="_blank" rel="noopener noreferrer">Croatia Private Tour</a>
          <a href="https://sailarmada.com/tour/choose-your-sailing-week-turkey/" target="_blank" rel="noopener noreferrer">Turkey Private Tour</a>
          <span className="sa-mobile-section">Group Sailing</span>
          <a href="https://sailarmada.com/group-sailing-holidays/" target="_blank" rel="noopener noreferrer">Why GROUP?</a>
          <a href="https://sailarmada.com/tour/discover-tahiti/" target="_blank" rel="noopener noreferrer">Discover Tahiti / French Polynesia</a>
          <a href="https://sailarmada.com/tour/watermelon/" target="_blank" rel="noopener noreferrer">Watermelon Croatia #18</a>
          <a href="https://sailarmada.com/tour/discover/" target="_blank" rel="noopener noreferrer">Discover Sardinia</a>
          <a href="https://sailarmada.com/tour/velactive/" target="_blank" rel="noopener noreferrer">Velactive – Sailing Active Escape</a>
          <a href="https://sailarmada.com/tour/watermelon-greece/" target="_blank" rel="noopener noreferrer">Watermelon Greece #19</a>
          <a href="https://sailarmada.com/tour/wildroute/" target="_blank" rel="noopener noreferrer">Wild Route Dodecanese</a>
          <Link to="/" onClick={() => setMobileOpen(false)} style={{ textDecoration: 'none', color: 'inherit' }}>Find a Yacht</Link>
          <span className="sa-mobile-section">Discover</span>
          <a href="https://sailarmada.com/reviews/" target="_blank" rel="noopener noreferrer">Customer Reviews</a>
          <a href="https://sailarmada.com/ambassadors/" target="_blank" rel="noopener noreferrer">Ambassadors</a>
          <a href="https://sailarmada.com/blog/" target="_blank" rel="noopener noreferrer">Blog</a>
          <a href="https://sailarmada.com/destinations/" target="_blank" rel="noopener noreferrer">Destinations</a>
          <a href="https://sailarmada.com/destinations/greece/" target="_blank" rel="noopener noreferrer" className="sa-indent">› Greece</a>
          <a href="https://sailarmada.com/destinations/malta/" target="_blank" rel="noopener noreferrer" className="sa-indent">› Malta</a>
          <a href="https://sailarmada.com/destinations/italy/" target="_blank" rel="noopener noreferrer" className="sa-indent">› Italy</a>
          <a href="https://sailarmada.com/destinations/turkey/" target="_blank" rel="noopener noreferrer" className="sa-indent">› Turkey</a>
          <a href="https://sailarmada.com/contact/" target="_blank" rel="noopener noreferrer">Contact</a>
          <Link to="/help" onClick={() => setMobileOpen(false)} style={{ textDecoration: 'none', color: 'inherit' }}>Help</Link>
          <span className="sa-mobile-section">Language</span>
          {languages.map(lang => (
            <button
              key={lang.code}
              className="sa-mobile-lang-btn"
              onClick={() => handleLanguageChange(lang.code)}
              style={lang.code === selectedLang ? { fontWeight: 'bold' } : {}}
            >
              <img
                src={`${FLAG_BASE}${lang.flag}`}
                alt={lang.label}
                width={18}
                height={14}
                style={{ verticalAlign: 'middle', marginRight: 6 }}
              />
              {lang.label}{lang.code === selectedLang ? ' ✓' : ''}
            </button>
          ))}
        </nav>
      )}
    </header>
  );
};

export default SiteHeader;
